import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, PlugZap, Save } from "lucide-react";

const FIELDS = [
  ["access_key_id", "Access Key ID", "AKIA..."],
  ["region", "Region", "ap-southeast-2"],
  ["bucket", "Nama Bucket", "nama-bucket"],
  ["prefix", "Folder", "contoh: pengepul-abg (opsional)"],
];

export default function S3Settings() {
  const [form, setForm] = useState({ access_key_id: "", region: "", bucket: "", prefix: "" });
  const [secret, setSecret] = useState("");
  const [hasSecret, setHasSecret] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api
      .get("/settings/s3")
      .then(({ data }) => {
        const { has_secret, ...rest } = data;
        setForm(rest);
        setHasSecret(has_secret);
      })
      .finally(() => setLoading(false));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put("/settings/s3", { ...form, secret_access_key: secret || null });
      toast.success("Pengaturan S3 disimpan");
      if (secret) setHasSecret(true);
      setSecret("");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/settings/s3/test");
      toast.success(data.message);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className="p-8" data-testid="s3-settings-page">
      <div className="mb-8 fade-up">
        <span className="text-sm uppercase tracking-[0.2em] text-muted-foreground">Pengaturan</span>
        <h1 className="mt-2 font-heading text-4xl font-bold tracking-tight">Penyimpanan S3</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Lokasi penyimpanan foto mitra Ekstra. Bucket harus bisa dibaca publik.
        </p>
      </div>
      {loading ? (
        <Loader2 className="h-5 w-5 animate-spin text-primary" />
      ) : (
        <form onSubmit={save} className="max-w-xl space-y-5 rounded-sm border border-border bg-surface/40 p-6 fade-up" data-testid="s3-settings-form">
          {FIELDS.map(([k, label, ph]) => (
            <div key={k} className="space-y-1.5">
              <Label htmlFor={`s3-${k}`}>{label}</Label>
              <Input
                id={`s3-${k}`}
                value={form[k] || ""}
                onChange={(e) => setForm((f) => ({ ...f, [k]: e.target.value }))}
                placeholder={ph}
                required={k !== "prefix"}
                data-testid={`s3-${k}-input`}
              />
            </div>
          ))}
          <div className="space-y-1.5">
            <Label htmlFor="s3-secret">Secret Access Key</Label>
            <Input
              id="s3-secret"
              type="password"
              value={secret}
              onChange={(e) => setSecret(e.target.value)}
              placeholder={hasSecret ? "•••••••• (tersimpan — isi untuk mengganti)" : "Secret Access Key"}
              required={!hasSecret}
              autoComplete="new-password"
              data-testid="s3-secret-input"
            />
            {hasSecret && (
              <p className="text-xs text-muted-foreground" data-testid="s3-secret-saved-hint">
                Secret Key sudah tersimpan dan tidak ditampilkan.
              </p>
            )}
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} data-testid="s3-save-button">
              {saving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Simpan
            </Button>
            <Button type="button" variant="outline" onClick={test} disabled={testing || !hasSecret} data-testid="s3-test-button">
              {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <PlugZap className="mr-2 h-4 w-4" />}
              Tes Koneksi
            </Button>
          </div>
        </form>
      )}
    </div>
  );
}
